import ArticlePageLayout from '@/components/feature/ArticlePageLayout';
import { Link } from 'react-router-dom';

const pageData = {
  breadcrumbTitle: 'How to Use Sugargoo Spreadsheet',
  category: 'Tutorial',
  title: 'How to Use Sugargoo Spreadsheet: Beginner Step-by-Step Tutorial 2026',
  excerpt: 'A complete beginner-friendly walkthrough on navigating Sugargoo Spreadsheets, placing orders through Sugargoo agent, and managing your first international haul from start to finish.',
  readTime: '14 min read',
  heroImageUrl: 'https://readdy.ai/api/search-image?query=Hands%20typing%20on%20laptop%20with%20notebook%20and%20smartphone%20on%20warm%20cream%20desk%2C%20step%20by%20step%20guide%20concept%2C%20soft%20natural%20daylight%2C%20cozy%20workspace%20aesthetic%2C%20neutral%20warm%20tones&width=1200&height=514&seq=article-hero-howto&orientation=landscape',
  heroImageAlt: 'How to use Sugargoo Spreadsheet - beginner tutorial guide',
  schemaData: {
    headline: 'How to Use Sugargoo Spreadsheet: Beginner Step-by-Step Tutorial 2026',
    description: 'Complete beginner-friendly walkthrough on navigating Sugargoo Spreadsheets, placing orders, and managing your first international haul.',
    datePublished: '2026-08-04',
    dateModified: '2026-08-04',
  },
};

export default function ArticleHowToUse() {
  return (
    <ArticlePageLayout
      breadcrumbTitle={pageData.breadcrumbTitle}
      category={pageData.category}
      title={pageData.title}
      excerpt={pageData.excerpt}
      readTime={pageData.readTime}
      heroImageUrl={pageData.heroImageUrl}
      heroImageAlt={pageData.heroImageAlt}
      schemaData={pageData.schemaData}
    >
      <section>
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
          Getting Started: What You Need Before Using a Sugargoo Spreadsheet
        </h2>
        <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
          Learning <strong className="text-foreground-800">how to use Sugargoo Spreadsheet</strong> resources is the fastest way to go from complete beginner to confident international shopper. Before diving into spreadsheets, you need three things: a Sugargoo account, a basic understanding of how shopping agents work, and access to a curated <strong className="text-foreground-800">Sugargoo Spreadsheet</strong> collection.
        </p>
        <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
          Creating a Sugargoo account is free and straightforward. Visit the Sugargoo website, register with your email, and you are ready to start. No payment method is required during registration — you only add funds when you are ready to place your first order through the <strong className="text-foreground-800">Sugargoo Spreadsheet</strong> links you discover.
        </p>
      </section>

      <section>
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
          Step-by-Step: How to Use Sugargoo Spreadsheet Links
        </h2>
        <ol className="space-y-4 list-decimal pl-5">
          <li className="text-sm md:text-base text-foreground-600 leading-relaxed">
            <strong className="text-foreground-800 block mb-1">Browse the Spreadsheet Categories</strong>
            Open the <strong className="text-foreground-800">Sugargoo Spreadsheet</strong> and navigate to your desired category. Each row represents one product find, typically with columns for product name, price estimate, size notes, and the direct purchase link.
          </li>
          <li className="text-sm md:text-base text-foreground-600 leading-relaxed">
            <strong className="text-foreground-800 block mb-1">Copy the Product Link</strong>
            Find an item that interests you and copy the link from the spreadsheet. This link goes directly to the product listing on Taobao, Weidian, or 1688.
          </li>
          <li className="text-sm md:text-base text-foreground-600 leading-relaxed">
            <strong className="text-foreground-800 block mb-1">Paste into Sugargoo Search Bar</strong>
            Log into your Sugargoo account and paste the link into the search bar. Sugargoo will parse the product page and display the item details, available sizes, colors, and prices.
          </li>
          <li className="text-sm md:text-base text-foreground-600 leading-relaxed">
            <strong className="text-foreground-800 block mb-1">Select Options and Add to Cart</strong>
            Choose your size, color, and quantity. Pay attention to the spreadsheet creator&apos;s size notes — many include recommendations like &quot;size up once&quot; or &quot;true to size.&quot;
          </li>
          <li className="text-sm md:text-base text-foreground-600 leading-relaxed">
            <strong className="text-foreground-800 block mb-1">Fund Your Account and Place Order</strong>
            Add funds via PayPal or other supported payment methods. Submit your order. Sugargoo purchases the item from the seller on your behalf.
          </li>
          <li className="text-sm md:text-base text-foreground-600 leading-relaxed">
            <strong className="text-foreground-800 block mb-1">Review QC Photos</strong>
            When the item arrives at the warehouse, check the QC photos. Accept the item or request a return if unsatisfied.
          </li>
          <li className="text-sm md:text-base text-foreground-600 leading-relaxed">
            <strong className="text-foreground-800 block mb-1">Consolidate and Ship</strong>
            When ready, select stored items and submit for international shipping. Choose your preferred carrier based on cost and speed.
          </li>
        </ol>
      </section>

      <section>
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
          Tips for First-Time Sugargoo Spreadsheet Users
        </h2>
        <ul className="space-y-3 list-disc pl-5">
          <li className="text-sm md:text-base text-foreground-600 leading-relaxed">
            <strong className="text-foreground-800">Start small:</strong> Your first order should be 1-2 inexpensive items. Mastering <strong className="text-foreground-800">how to use Sugargoo Spreadsheet</strong> links takes practice.
          </li>
          <li className="text-sm md:text-base text-foreground-600 leading-relaxed">
            <strong className="text-foreground-800">Read spreadsheet notes carefully:</strong> Good spreadsheet creators include sizing notes, quality ratings, and seller reliability notes. These are invaluable for beginners.
          </li>
          <li className="text-sm md:text-base text-foreground-600 leading-relaxed">
            <strong className="text-foreground-800">Use rehearsal shipping:</strong> Before your first international shipment, pay for rehearsal shipping to get exact weight and cost estimates.
          </li>
        </ul>
        <p className="text-sm md:text-base text-foreground-600 leading-relaxed mt-4">
          Ready to explore? Browse the <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium">category page</Link> or read our <Link to="/article/sugargoo-shipping-cost-guide" className="text-primary-500 hover:text-primary-600 font-medium">shipping cost guide</Link> to understand shipping options.
        </p>
      </section>
    </ArticlePageLayout>
  );
}